/**
 * Comparison workspace — M3 Phase 3.3 (US-105 / US-106 core view).
 *
 * Replaces the M2 placeholder with the real diff renderer:
 *
 *   - Loads the comparison record + both versions' parsed DocNode trees.
 *   - Drives `SideBySideView` (default) or `UnifiedView` based on a toggle
 *     persisted to localStorage so the user's last choice survives reloads.
 *   - Surfaces a quick change-count summary in the top bar.
 *   - Shows a status pill while the comparison is still processing or has
 *     errored out, instead of rendering empty diff trees.
 *
 * M3 Phases 3.4-3.6 will layer keyboard navigation, focus mode and Accept /
 * Reject controls on top of this scaffolding without restructuring the
 * page-level data flow.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChangeListPanel,
  Cheatsheet,
  ExportMenu,
  FilterPopover,
  Minimap,
  SideBySideView,
  UnifiedView,
  summariseChanges,
  useAnnotations,
  useChangeFilter,
  useChangeListPanel,
  useChangeNavigation,
  useDecisions,
  useFocusReadMode,
  useKeyboardShortcuts,
  type ChangeStats,
  type DecisionAdapter,
  type NavigationAdapter,
  type UseAnnotationsResult,
  type UseChangeFilterResult,
  type UseChangeListPanelResult,
  type UseDecisionsResult,
  type UseFocusReadModeResult,
} from './features/compare';
import { EditingView } from './features/editing';
import { SourceVerificationView } from './features/source-verification';
import { ChangeAnalysisView } from './features/change-analysis';
import { VisibleChangesContext } from './features/compare/VisibleChangesContext';
import { ToastProvider, useToast } from './components/Toast';
import {
  veridocApi,
  type ChangeListResponse,
  type ComparisonMetaResponse,
  type ComparisonResponse,
  type CurrentUser,
  type DecisionAction,
  type DecisionStatus,
  type DocumentVersionDetail,
  type VeriDocRole,
} from './lib/veridoc-api';
import { ApiError } from './lib/transport';
import type {
  ComparisonDecisionInfo,
  ComparisonLoadError,
  ComparisonReadyInfo,
} from './embed/events';
import type { DocNode } from './features/doc-viewer/DocViewer.types';

type ViewMode =
  | 'side-by-side'
  | 'unified'
  | 'editing'
  | 'source-verification'
  | 'change-analysis';
const VIEW_MODE_STORAGE_KEY = 'veridoc:comparison:viewMode';

function loadViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'side-by-side';
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  if (stored === 'unified') return 'unified';
  if (stored === 'editing') return 'editing';
  if (stored === 'source-verification') return 'source-verification';
  if (stored === 'change-analysis') return 'change-analysis';
  return 'side-by-side';
}

function saveViewMode(mode: ViewMode): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
}

export interface ComparisonWorkspaceProps {
  /** Comparison record id the workspace renders — host-supplied (navigation seam). */
  comparisonId: string;
  /** Authenticated user, or null while loading / anonymous (identity seam). */
  user: CurrentUser | null;
  /** Invoked when the user leaves the workspace ("back to library"). */
  onExit: () => void;
  /** Optional brand-mark click handler. Omit to render the logo inert (embed). */
  onHome?: () => void;
  /**
   * Output seam (ADR-119 D4): fired AT MOST ONCE per comparisonId, after the
   * comparison record + both version trees have loaded. `status` carries the
   * backend comparison state — the workspace still mounts and renders a status
   * banner for `processing` / `error`, so those are "ready", not failures.
   * StrictMode double-invoke safe. A failed initial fetch fires `onLoadError`
   * instead, not this. Optional; demo / non-observing hosts omit it.
   */
  onReady?: (info: ComparisonReadyInfo) => void;
  /**
   * Output seam: fired when the initial data fetch fails (network / HTTP /
   * auth) before any comparison data is available. Optional.
   */
  onLoadError?: (err: ComparisonLoadError) => void;
  /**
   * Output seam: fired after a single change decision is persisted
   * (server-confirmed). `action` is the resulting state, not the verb pressed
   * (re-asserting an accepted change reports `pending`). Optimistic writes that
   * roll back never fire this. Batch decisions do not fire it (R4). Optional.
   */
  onDecision?: (decision: ComparisonDecisionInfo) => void;
}

/** Map a server-authoritative decision status to the action verb the embed
 * contract exposes (`ComparisonDecisionInfo.action`). */
function statusToAction(status: DecisionStatus): DecisionAction {
  if (status === 'accepted') return 'accept';
  if (status === 'rejected') return 'reject';
  return 'pending';
}

export default function ComparisonWorkspace(
  props: ComparisonWorkspaceProps,
): React.JSX.Element {
  // Self-hold the toast provider so a host embedding <ComparisonWorkspace>
  // never has to mount one (R3a Step 4 / ADR-119 D3). Every useToast() call in
  // the workspace subtree resolves to this instance.
  return (
    <ToastProvider>
      <ComparisonWorkspaceInner {...props} />
    </ToastProvider>
  );
}

function ComparisonWorkspaceInner({
  comparisonId,
  user,
  onExit,
  onHome,
  onReady,
  onLoadError,
  onDecision,
}: ComparisonWorkspaceProps): React.JSX.Element {
  const toast = useToast();
  const [comparison, setComparison] = useState<ComparisonResponse | null>(null);
  const [versionA, setVersionA] = useState<DocumentVersionDetail | null>(null);
  const [versionB, setVersionB] = useState<DocumentVersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());

  // Latest-callback refs: read the freshest output-seam callbacks from inside
  // the load effect / decision handler without putting them (or `user`) in the
  // effect deps — otherwise the data fetch would re-run when a callback
  // identity or the user changes. `onReadyFiredForRef` enforces the
  // "at most once per comparisonId" guarantee (StrictMode-safe).
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;
  const onDecisionRef = useRef(onDecision);
  onDecisionRef.current = onDecision;
  const userRef = useRef(user);
  userRef.current = user;
  const onReadyFiredForRef = useRef<string | null>(null);

  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!comparisonId) return;
    let alive = true;
    (async () => {
      try {
        // T16: the comparison payload is split into metadata + paged
        // changes. The workspace still consumes the legacy
        // ``ComparisonResponse`` shape internally (the downstream hooks
        // depend on `changes[]`), so we fetch the two new endpoints in
        // parallel and synthesise the compat shape. The version fetches
        // depend on the meta's version ids, so they race after the meta
        // resolves. The paged endpoint caps at ``limit=200`` — every
        // seeded contract diff is well under that, and a follow-up task
        // will replace this with proper paging once the change list
        // grows past the cap.
        const meta = await veridocApi.getComparisonMeta(comparisonId);
        const [changesPage, versionA, versionB] = await Promise.all([
          veridocApi.getComparisonChanges(comparisonId, { limit: 200 }),
          veridocApi.getVersion(meta.versionAId),
          veridocApi.getVersion(meta.versionBId),
        ]);
        if (!alive) return;
        const cmp = synthesiseComparisonResponse(meta, changesPage);
        setComparison(cmp);
        setVersionA(versionA);
        setVersionB(versionB);
        // Output seam — fire onReady at most once per comparisonId. The
        // `alive` guard above means an aborted (e.g. StrictMode-cleaned) run
        // never reaches here, and the ref guard de-dupes any same-instance
        // re-run.
        if (onReadyFiredForRef.current !== comparisonId) {
          onReadyFiredForRef.current = comparisonId;
          onReadyRef.current?.({
            changeCount: cmp.changes.length,
            status: cmp.status,
            userRole: userRef.current?.role ?? null,
          });
        }
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
        const apiErr = err instanceof ApiError ? err : null;
        onLoadErrorRef.current?.({
          message: err instanceof Error ? err.message : String(err),
          status: apiErr?.status ?? 0,
          code: apiErr?.code,
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [comparisonId]);

  // Memoised list of changes — the hook re-seeds whenever this reference
  // changes, so we *must* keep it stable across re-renders that don't
  // actually swap comparisons.
  const changes = useMemo(
    () => comparison?.changes ?? EMPTY_CHANGES,
    [comparison],
  );

  // Stable bridge from the hook's server-confirmed callback to the
  // host-facing `onDecision` prop (mapping final status → action verb). Stable
  // identity keeps `useDecisions().decide` from churning each render.
  const handleHookDecision = useCallback(
    (changeId: string, finalStatus: DecisionStatus) => {
      onDecisionRef.current?.({ changeId, action: statusToAction(finalStatus) });
    },
    [],
  );

  const decisions = useDecisions({
    comparisonId,
    initialChanges: changes,
    onError: (msg) => toast.show(msg, 'danger'),
    onDecision: handleHookDecision,
  });

  const filter = useChangeFilter({
    changes,
    getStatus: decisions.getStatus,
    statusVersion: decisions.statusVersion,
  });

  const annotations = useAnnotations({
    comparisonId,
    canAuthor: !!user && user.role !== 'viewer',
    currentUserId: user?.id,
    onError: (msg) => toast.show(msg, 'danger'),
  });

  // Cross-role visibility: the GET /decisions response is the source of
  // truth when the *user* changes (e.g. role-switch happens in another
  // tab and this tab navigates back). We refetch once per role flip.
  useEffect(() => {
    if (!comparisonId || !user) return;
    void decisions.refresh();
    // We deliberately only re-run on role change. The hook already seeded
    // initial state from `comparison.changes[].currentStatus`, so this
    // refresh is purely a tie-breaker for stale tabs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonId, user?.role]);

  const stats = useMemo(
    () => (comparison ? summariseChanges(comparison.changes) : null),
    [comparison],
  );

  const filteredChangeIds = useMemo(
    () => filter.filteredChanges.map((c) => c.id),
    [filter.filteredChanges],
  );

  // Adapter passed down to the views — narrow surface so the views stay
  // mostly presentational and easy to reason about.
  const decisionAdapter: DecisionAdapter | undefined = useMemo(() => {
    if (!user) return undefined;
    return {
      getStatus: decisions.getStatus,
      getMeta: decisions.getMeta,
      isPending: decisions.isPending,
      decide: (changeId, action, note) => {
        void decisions.decide(changeId, action, note);
      },
      canDecide: user.role !== 'viewer',
    };
  }, [user, decisions.getStatus, decisions.getMeta, decisions.isPending, decisions.decide]);

  // ---- Phase 3.4: navigation + keyboard shortcuts + cheatsheet -----------
  const navigation = useChangeNavigation({ changes: filter.filteredChanges });
  const [cheatsheetOpen, setCheatsheetOpen] = useState<boolean>(false);
  const panel = useChangeListPanel();

  const navigationAdapter: NavigationAdapter = useMemo(
    () => ({
      register: navigation.register,
      selectedChangeId: navigation.selectedChangeId,
      select: navigation.select,
    }),
    [navigation.register, navigation.selectedChangeId, navigation.select],
  );

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) =>
      prev === 'side-by-side' ? 'unified' : 'side-by-side',
    );
  }, []);

  // Phase 3.5: real focus-mode adapter. The hook owns localStorage
  // persistence and the temp-activation timer map; the keyboard layer
  // only needs `toggle()` so we forward that to the shortcuts hook.
  const focus = useFocusReadMode();

  const showCheatsheet = useCallback(() => setCheatsheetOpen(true), []);
  const hideCheatsheet = useCallback(() => setCheatsheetOpen(false), []);

  // P3-26: stable error handler for the three non-default panes so they can
  // be wrapped in React.memo. Without this the inline `(msg) => toast.show(...)`
  // closure flips identity on every parent render, defeating any memoization
  // and forcing EditingPane / SourceVerificationPane / ChangeAnalysisPane to
  // re-render even when only the comparison object reference changed.
  const showErrorToast = useCallback(
    (msg: string) => toast.show(msg, 'danger'),
    [toast],
  );

  useKeyboardShortcuts({
    navigation,
    decisions: decisionAdapter,
    view: { toggle: toggleViewMode },
    focus: { toggle: focus.toggle },
    onShowCheatsheet: showCheatsheet,
    onHideCheatsheet: hideCheatsheet,
  });

  const hasChanges = !!comparison && comparison.changes.length > 0;

  return (
    <main className="relative flex h-screen flex-col bg-ink-50 text-ink-900">
      <TopBar
        onHome={onHome}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        stats={stats}
        progress={decisions.progress}
        filter={filter}
        onBack={onExit}
        onShowCheatsheet={showCheatsheet}
        focus={focus}
        panel={panel}
        batchDecide={decisions.batchDecide}
        batchPending={decisions.batchPending}
        canDecide={!!user && user.role !== 'viewer'}
        filteredChangeIds={filteredChangeIds}
        comparisonId={comparisonId}
      />

      <div className="flex min-h-0 flex-1">
        {/* Left panel: change list or collapsed toggle strip */}
        {hasChanges && (
          <ChangeListPanel
            changes={filter.filteredChanges}
            allChangesCount={comparison!.changes.length}
            stats={stats!}
            decisions={decisionAdapter}
            navigation={navigationAdapter}
            panel={panel}
            isFiltering={filter.isFiltering}
            searchTerm={filter.searchTerm}
            onSearchTermChange={filter.setSearchTerm}
          />
        )}

        {/* Main content: scrolls independently */}
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1400px] px-6 py-8 pr-20">
            {error && (
              <p className="mb-4 rounded-md bg-semantic-dangerBg px-4 py-3 text-sm text-semantic-danger">
                {error}
              </p>
            )}

            {!comparison ? (
              <CenteredMessage text="加载比对…" />
            ) : viewMode === 'editing' ? (
              <EditingPane
                comparison={comparison}
                comparisonId={comparisonId}
                userRole={user?.role ?? null}
                onError={showErrorToast}
              />
            ) : viewMode === 'source-verification' ? (
              <SourceVerificationPane
                comparison={comparison}
                comparisonId={comparisonId}
                userRole={user?.role ?? null}
                active={viewMode === 'source-verification'}
                onError={showErrorToast}
              />
            ) : viewMode === 'change-analysis' ? (
              <ChangeAnalysisPane
                comparison={comparison}
                comparisonId={comparisonId}
                userRole={user?.role ?? null}
                active={viewMode === 'change-analysis'}
                onError={showErrorToast}
              />
            ) : (
              <ComparisonBody
                comparison={comparison}
                filteredChanges={filter.filteredChanges}
                versionA={versionA}
                versionB={versionB}
                viewMode={viewMode}
                decisions={decisionAdapter}
                navigation={navigationAdapter}
                focus={focus}
                annotations={annotations}
                isFiltering={filter.isFiltering}
                onResetFilters={filter.resetFilters}
              />
            )}
          </div>
        </div>
      </div>

      {hasChanges && (
        <VisibleChangesContext.Provider value={navigation.visibleChangeIds}>
          <Minimap
            changes={filter.filteredChanges}
            navigation={navigation}
            decisions={decisionAdapter}
          />
        </VisibleChangesContext.Provider>
      )}

      <Cheatsheet open={cheatsheetOpen} onClose={hideCheatsheet} />
    </main>
  );
}

// Shared empty-array reference — keeps the `useDecisions` fingerprint stable
// while a comparison is still loading.
const EMPTY_CHANGES: ComparisonResponse['changes'] = [];

/**
 * T16 split-payload glue: merge the metadata-only response with the paged
 * change list back into the legacy :type:`ComparisonResponse` shape the
 * downstream hooks (``useDecisions`` / ``useChangeNavigation`` / etc.)
 * still consume. Avoids a wider refactor of the workspace internals
 * during the 2-week endpoint sunset window.
 *
 * Aggregate counts (``acceptedCount`` / ``rejectedCount`` /
 * ``annotationCount``) are intentionally dropped here — the TopBar's
 * :component:`ProgressBadge` derives ``decided / accepted / rejected``
 * counts from the local decision map (see ``useDecisions.progress``),
 * not from the server's precomputed aggregate. ``useAnnotations`` reuses
 * the per-change counts it already fetches; the comparison-level total
 * is not needed by any current consumer.
 */
function synthesiseComparisonResponse(
  meta: ComparisonMetaResponse,
  changesPage: ChangeListResponse,
): ComparisonResponse {
  return {
    id: meta.id,
    versionAId: meta.versionAId,
    versionBId: meta.versionBId,
    name: meta.name,
    status: meta.status,
    createdBy: meta.createdBy,
    createdByRole: meta.createdByRole,
    changeCount: meta.changeCount,
    createdAt: meta.createdAt,
    completedAt: meta.completedAt,
    errorCode: meta.errorCode,
    errorMessage: meta.errorMessage,
    changes: changesPage.items,
  };
}

const ComparisonBody = memo(function ComparisonBody({
  comparison,
  filteredChanges,
  versionA,
  versionB,
  viewMode,
  decisions,
  navigation,
  focus,
  annotations,
  isFiltering,
  onResetFilters,
}: {
  comparison: ComparisonResponse;
  filteredChanges: ComparisonResponse['changes'];
  versionA: DocumentVersionDetail | null;
  versionB: DocumentVersionDetail | null;
  viewMode: ViewMode;
  decisions: DecisionAdapter | undefined;
  navigation: NavigationAdapter;
  focus: UseFocusReadModeResult;
  annotations: UseAnnotationsResult;
  isFiltering: boolean;
  onResetFilters: () => void;
}): React.JSX.Element {
  if (comparison.status === 'processing') {
    return (
      <StatusBanner
        tone="info"
        title="比对引擎仍在工作"
        body="后端正在分析两个版本的差异，稍候片刻后刷新本页即可看到完整结果。"
      />
    );
  }

  if (comparison.status === 'error') {
    return (
      <StatusBanner
        tone="error"
        title="比对失败"
        body={
          comparison.errorMessage ??
          '比对引擎返回错误，请检查文档是否解析就绪后重新发起比对。'
        }
      />
    );
  }

  if (!versionA || !versionB) {
    return <CenteredMessage text="加载版本…" />;
  }

  const treeA = versionA.parsedDoc as DocNode | null;
  const treeB = versionB.parsedDoc as DocNode | null;

  if (!treeA || !treeB) {
    return (
      <StatusBanner
        tone="warning"
        title="文档解析尚未就绪"
        body="比对依赖 PDF 解析结果，刷新本页或重新触发解析后即可继续。"
      />
    );
  }

  if (comparison.changes.length === 0) {
    return (
      <>
        <StatusBanner
          tone="success"
          title="两个版本完全一致"
          body="比对引擎未发现任何差异。下方仅展示 v2 内容供参考。"
        />
        <div className="mt-6 rounded-xl border border-ink-200 bg-ink-0 px-2 py-6 shadow-sm">
          <UnifiedView treeA={treeA} treeB={treeB} changes={[]} focus={focus} />
        </div>
      </>
    );
  }

  // All changes exist but none match the active filters.
  if (filteredChanges.length === 0 && isFiltering) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-ink-200 bg-ink-0 px-6 py-12 text-center shadow-sm">
        <p className="text-sm text-ink-500">当前过滤条件下无匹配变更。</p>
        <button
          type="button"
          onClick={onResetFilters}
          className="rounded-sm border border-ink-200 bg-ink-50 px-3 py-1.5 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
        >
          重置过滤条件
        </button>
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-ink-200 bg-ink-0 px-2 py-6 shadow-sm">
      {viewMode === 'side-by-side' ? (
        <SideBySideView
          treeA={treeA}
          treeB={treeB}
          changes={filteredChanges}
          decisions={decisions}
          navigation={navigation}
          focus={focus}
          annotations={annotations}
        />
      ) : (
        <UnifiedView
          treeA={treeA}
          treeB={treeB}
          changes={filteredChanges}
          decisions={decisions}
          navigation={navigation}
          focus={focus}
          annotations={annotations}
        />
      )}
    </section>
  );
});

// P3-26: the three non-default panes are wrapped in React.memo. They only
// re-render when their props actually change. The previous inline
// `(msg) => toast.show(...)` closure flipped identity on every parent
// render; the now-stable `showErrorToast` callback lets memo do its job.
// Each pane branches on `comparison.status` first so the heavy inner view
// (EditingView / SourceVerificationView / ChangeAnalysisView) never mounts
// while the comparison is still processing or has errored.
const EditingPane = memo(function EditingPane({
  comparison,
  comparisonId,
  userRole,
  onError,
}: {
  comparison: ComparisonResponse;
  comparisonId: string;
  userRole: VeriDocRole | null;
  onError: (message: string) => void;
}): React.JSX.Element {
  if (comparison.status === 'processing') {
    return (
      <StatusBanner
        tone="info"
        title="比对引擎仍在工作"
        body="定稿编辑需要比对完成后才能进入，稍候刷新本页。"
      />
    );
  }
  if (comparison.status === 'error') {
    return (
      <StatusBanner
        tone="error"
        title="比对失败"
        body={
          comparison.errorMessage ??
          '比对引擎返回错误，无法进入定稿编辑。'
        }
      />
    );
  }
  return (
    <section className="rounded-xl border border-ink-200 bg-ink-0 px-2 py-6 shadow-sm">
      <EditingView
        comparisonId={comparisonId}
        userRole={userRole}
        active
        onError={onError}
      />
    </section>
  );
});

const SourceVerificationPane = memo(function SourceVerificationPane({
  comparison,
  comparisonId,
  userRole,
  active,
  onError,
}: {
  comparison: ComparisonResponse;
  comparisonId: string;
  userRole: VeriDocRole | null;
  active: boolean;
  onError: (message: string) => void;
}): React.JSX.Element {
  if (comparison.status === 'processing') {
    return (
      <StatusBanner
        tone="info"
        title="比对引擎仍在工作"
        body="来源核对需要比对完成后才能发起，稍候刷新本页。"
      />
    );
  }
  if (comparison.status === 'error') {
    return (
      <StatusBanner
        tone="error"
        title="比对失败"
        body={
          comparison.errorMessage ??
          '比对引擎返回错误，无法发起来源核对。'
        }
      />
    );
  }
  return (
    <SourceVerificationView
      comparisonId={comparisonId}
      userRole={userRole}
      active={active}
      onError={onError}
    />
  );
});

const ChangeAnalysisPane = memo(function ChangeAnalysisPane({
  comparison,
  comparisonId,
  userRole,
  active,
  onError,
}: {
  comparison: ComparisonResponse;
  comparisonId: string;
  userRole: VeriDocRole | null;
  active: boolean;
  onError: (message: string) => void;
}): React.JSX.Element {
  if (comparison.status === 'processing') {
    return (
      <StatusBanner
        tone="info"
        title="比对引擎仍在工作"
        body="变更分析需要比对完成后才能发起，稍候刷新本页。"
      />
    );
  }
  if (comparison.status === 'error') {
    return (
      <StatusBanner
        tone="error"
        title="比对失败"
        body={
          comparison.errorMessage ??
          '比对引擎返回错误，无法发起变更分析。'
        }
      />
    );
  }
  return (
    <ChangeAnalysisView
      comparisonId={comparisonId}
      userRole={userRole}
      active={active}
      onError={onError}
    />
  );
});

const TopBar = memo(function TopBar({
  onHome,
  viewMode,
  onViewModeChange,
  stats,
  progress,
  filter,
  onBack,
  onShowCheatsheet,
  focus,
  panel,
  batchDecide,
  batchPending,
  canDecide,
  filteredChangeIds,
  comparisonId,
}: {
  onHome?: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  stats: ChangeStats | null;
  progress: UseDecisionsResult['progress'];
  filter: UseChangeFilterResult;
  onBack: () => void;
  onShowCheatsheet: () => void;
  focus: UseFocusReadModeResult;
  panel: UseChangeListPanelResult;
  batchDecide: (changeIds: string[], action: DecisionAction) => Promise<void>;
  batchPending: boolean;
  canDecide: boolean;
  filteredChangeIds: string[];
  comparisonId: string;
}): React.JSX.Element {
  return (
    <nav className="sticky top-0 z-30 shrink-0 border-b border-ink-200 bg-ink-0">
      <div className="mx-auto flex h-12 max-w-[1400px] items-center justify-between gap-3 px-3">
        <div className="flex min-w-0 items-center gap-3">
          {onHome ? (
            <button
              type="button"
              onClick={onHome}
              className="shrink-0 text-xl font-semibold tracking-tight text-ink-900 hover:text-ink-700"
            >
              VeriDoc
            </button>
          ) : (
            <span className="text-xl font-semibold tracking-tight text-ink-900">
              VeriDoc
            </span>
          )}
          <span className="text-xs text-ink-300">/</span>
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 text-xs text-ink-600 hover:text-ink-900"
          >
            ← 返回文档库
          </button>
          {stats && stats.total > 0 && (
            <button
              type="button"
              onClick={panel.toggle}
              aria-pressed={panel.isOpen}
              title={panel.isOpen ? '折叠变更列表' : '展开变更列表'}
              aria-label={panel.isOpen ? '折叠变更列表' : '展开变更列表'}
              className="inline-flex h-btn-sm shrink-0 items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
            >
              <span aria-hidden="true" className="text-sm leading-none">☰</span>
              <span className="hidden md:inline">列表</span>
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {stats && stats.total > 0 && <ChangeCountBadge stats={stats} />}
          {progress.total > 0 && <ProgressBadge progress={progress} />}
          {stats && stats.total > 0 && (
            <FilterPopover
              typeFilter={filter.typeFilter}
              statusFilter={filter.statusFilter}
              semanticTagFilter={filter.semanticTagFilter}
              onToggleType={filter.toggleType}
              onToggleStatus={filter.toggleStatus}
              onToggleSemanticTag={filter.toggleSemanticTag}
              onReset={filter.resetFilters}
              isFiltering={filter.isFiltering}
              activeFilterCount={filter.activeFilterCount}
              stats={stats}
            />
          )}
          {canDecide && stats && stats.total > 0 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { void batchDecide(filteredChangeIds, 'accept'); }}
                disabled={batchPending || filteredChangeIds.length === 0}
                title={filter.isFiltering ? `接受已过滤 (${filteredChangeIds.length})` : '全部接受'}
                data-testid="batch-accept-button"
                className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-semantic-success hover:bg-semantic-successBg disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span aria-hidden="true">&#10003;</span>
                <span className="hidden md:inline">
                  {filter.isFiltering ? `接受 (${filteredChangeIds.length})` : '全部接受'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => { void batchDecide(filteredChangeIds, 'reject'); }}
                disabled={batchPending || filteredChangeIds.length === 0}
                title={filter.isFiltering ? `拒绝已过滤 (${filteredChangeIds.length})` : '全部拒绝'}
                data-testid="batch-reject-button"
                className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-semantic-danger hover:bg-semantic-dangerBg disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span aria-hidden="true">&#10005;</span>
                <span className="hidden md:inline">
                  {filter.isFiltering ? `拒绝 (${filteredChangeIds.length})` : '全部拒绝'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => { void batchDecide(filteredChangeIds, 'pending'); }}
                disabled={batchPending || filteredChangeIds.length === 0}
                title={filter.isFiltering ? `重置已过滤 (${filteredChangeIds.length})` : '全部待定'}
                data-testid="batch-pending-button"
                className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span aria-hidden="true">&#8634;</span>
                <span className="hidden md:inline">
                  {filter.isFiltering ? `待定 (${filteredChangeIds.length})` : '全部待定'}
                </span>
              </button>
            </div>
          )}
          <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
          <ExportMenu comparisonId={comparisonId} />
          <FocusToggleButton focus={focus} />
          <button
            type="button"
            onClick={onShowCheatsheet}
            title="查看快捷键 (?)"
            aria-label="查看快捷键"
            className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
          >
            <kbd className="font-mono text-xs">?</kbd>
            <span className="hidden md:inline">快捷键</span>
          </button>
        </div>
      </div>
    </nav>
  );
});

function ChangeCountBadge({
  stats,
}: {
  stats: ReturnType<typeof summariseChanges>;
}): React.JSX.Element {
  return (
    <span className="hidden items-center gap-2 text-xs text-ink-500 md:inline-flex">
      <span className="font-semibold text-ink-900">{stats.total} 处变更</span>
      <span className="text-ink-300">·</span>
      <Pill
        label={`${stats.additions} 增`}
        bg="bg-change-addition-bg"
        fg="text-change-addition-fg"
      />
      <Pill
        label={`${stats.deletions} 删`}
        bg="bg-change-deletion-bg"
        fg="text-change-deletion-fg"
      />
      <Pill
        label={`${stats.modifications} 改`}
        bg="bg-change-modification-bg"
        fg="text-change-modification-fg"
      />
      {stats.moves > 0 && (
        <Pill
          label={`${stats.moves} 移`}
          bg="bg-change-move-bg"
          fg="text-change-move-fg"
        />
      )}
      {stats.formats > 0 && (
        <Pill
          label={`${stats.formats} 格`}
          bg="bg-change-format-bg"
          fg="text-change-format-fg"
        />
      )}
    </span>
  );
}

function ProgressBadge({
  progress,
}: {
  progress: UseDecisionsResult['progress'];
}): React.JSX.Element {
  return (
    <span className="hidden items-center gap-2 text-xs text-ink-500 md:inline-flex">
      <span className="font-semibold text-ink-900">
        已决定 {progress.decided}/{progress.total}
      </span>
      <span className="text-ink-300">·</span>
      <Pill
        label={`${progress.accepted} 接`}
        bg="bg-semantic-successBg"
        fg="text-semantic-success"
      />
      <Pill
        label={`${progress.rejected} 拒`}
        bg="bg-semantic-dangerBg"
        fg="text-semantic-danger"
      />
    </span>
  );
}

function Pill({
  label,
  bg,
  fg,
}: {
  label: string;
  bg: string;
  fg: string;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium ${bg} ${fg}`}
    >
      {label}
    </span>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="比对视图模式"
      className="inline-flex h-btn-sm items-center rounded-md border border-ink-200 bg-ink-50 p-0.5 text-xs"
    >
      <ToggleButton
        active={value === 'side-by-side'}
        onClick={() => onChange('side-by-side')}
        label="双栏"
      />
      <ToggleButton
        active={value === 'unified'}
        onClick={() => onChange('unified')}
        label="统一"
      />
      <ToggleButton
        active={value === 'editing'}
        onClick={() => onChange('editing')}
        label="定稿编辑"
      />
      <ToggleButton
        active={value === 'source-verification'}
        onClick={() => onChange('source-verification')}
        label="来源核对"
      />
      <ToggleButton
        active={value === 'change-analysis'}
        onClick={() => onChange('change-analysis')}
        label="变更分析"
      />
    </div>
  );
}

function FocusToggleButton({
  focus,
}: {
  focus: UseFocusReadModeResult;
}): React.JSX.Element {
  // Visual states:
  //   off → ink-50 background, ink-600 text (matches `?` button neighbour)
  //   on  → brand-50 background, brand-700 text + brand border so the
  //         operator gets a clear "this is active" signal across the bar.
  const palette = focus.enabled
    ? 'border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-100'
    : 'border-ink-200 bg-ink-50 text-ink-600 hover:bg-ink-100 hover:text-ink-900';

  return (
    <button
      type="button"
      onClick={focus.toggle}
      aria-pressed={focus.enabled}
      title={focus.enabled ? '关闭聚焦阅读 (f)' : '开启聚焦阅读 (f)'}
      aria-label={focus.enabled ? '关闭聚焦阅读模式' : '开启聚焦阅读模式'}
      data-testid="focus-mode-toggle"
      className={`inline-flex h-btn-sm items-center gap-1 rounded-sm border px-2 text-xs transition-colors duration-fast ${palette}`}
    >
      <span aria-hidden="true">👁</span>
      <span className="hidden md:inline">聚焦</span>
      <kbd className="ml-1 hidden font-mono text-[10px] opacity-70 md:inline">
        f
      </kbd>
    </button>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}): React.JSX.Element {
  const base =
    'inline-flex h-6 items-center rounded px-3 text-xs font-medium transition-colors duration-fast';
  const tone = active
    ? 'bg-ink-0 text-ink-900 shadow-sm'
    : 'text-ink-500 hover:text-ink-700';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`${base} ${tone}`}
    >
      {label}
    </button>
  );
}

function StatusBanner({
  tone,
  title,
  body,
}: {
  tone: 'info' | 'success' | 'warning' | 'error';
  title: string;
  body: string;
}): React.JSX.Element {
  const palette: Record<typeof tone, { bg: string; fg: string; border: string }> = {
    info: {
      bg: 'bg-semantic-infoBg',
      fg: 'text-semantic-info',
      border: 'border-semantic-info',
    },
    success: {
      bg: 'bg-semantic-successBg',
      fg: 'text-semantic-success',
      border: 'border-semantic-success',
    },
    warning: {
      bg: 'bg-semantic-warningBg',
      fg: 'text-semantic-warning',
      border: 'border-semantic-warning',
    },
    error: {
      bg: 'bg-semantic-dangerBg',
      fg: 'text-semantic-danger',
      border: 'border-semantic-danger',
    },
  };
  const { bg, fg, border } = palette[tone];
  return (
    <div className={`rounded-md border ${border} ${bg} px-4 py-3`}>
      <p className={`text-sm font-semibold ${fg}`}>{title}</p>
      <p className="mt-1 text-xs text-ink-700">{body}</p>
    </div>
  );
}

function CenteredMessage({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] items-center justify-center text-sm text-ink-500"
    >
      {text}
    </div>
  );
}
